import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import InlineText from '@/design_system/Typography/InlineText';
import IconBag from '@/icons/Bag.svg';
import {
  BrandCreationLayout,
  BrandCreationLayoutHeader,
} from '@/layouts/Brand/BrandCreationLayout/BrandCreationLayout';
import {
  BrandRequestContextProvider,
  useBrandRequestContext,
} from '@/layouts/Brand/BrandRequestContext';
import { hasDefects } from '@/models/article';
import { useMediaQueries } from '@/models/medium';
import { useProductAttributesByArticle } from '@/models/productAttribute';
import {
  getNumberOfDefects,
  useClientRequest,
  useCreateDraftRequestArticle,
} from '@/models/request';
import { useWorkflowPublic } from '@/models/workflow';
import { useClientToken, useCurrentOrganization, useStoreToken } from '@/services/auth';
import { ErrorBoundary } from '@/services/sentry';
import { createBEMClasses } from '@/utils/classname';
import { REQUEST_IN_PROGRESS_LOCAL_STORAGE_KEY } from '@/utils/requestInProgress';

import AddAnotherDefect from './components/Article/components/AddAnotherDefect';
import ArticleForm from './components/Article/components/ArticleForm';
import DefectsForm from './components/Article/components/DefectsForm';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import SettingsForm from './components/SettingsForm';
import Welcome from './components/Welcome';

import './New.css';

const { block, element } = createBEMClasses('client-new-request');

const New = () => {
  const [organization] = useCurrentOrganization();
  const navigate = useNavigate();
  const clientToken = useClientToken();
  const storeToken = useStoreToken();

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') ?? '';

  const {
    data: request,
    isLoading: isRequestLoading,
    isError: isErrorRequest,
  } = useClientRequest(id);
  const { data: workflow } = useWorkflowPublic(request?.workflowId);
  const productAttributesByArticle = useProductAttributesByArticle(request);

  useEffect(() => {
    if (isErrorRequest) {
      navigate('', { replace: true });
    }
  }, [isErrorRequest, navigate]);

  // Redirect to the request page if the request is not a draft
  useEffect(() => {
    if (request?.draft === false) {
      const tokenQueryParam = clientToken
        ? `clientToken=${clientToken}`
        : `storeToken=${storeToken}`;

      navigate(`/brand/${organization!.slug}/requests/${request.id}?${tokenQueryParam}`, {
        replace: true,
      });
    }
  }, [organization, request, navigate, clientToken, storeToken]);

  const [showCart, setShowCart] = useState(false);

  const [globalStep, setGlobalStep] = useState<'create' | 'checkout'>('create');

  return (
    <BrandCreationLayout>
      <BrandCreationLayoutHeader>
        {request && globalStep === 'create' && (
          <div className={element('cart-button')}>
            <Button
              iconOnly
              noBorder
              variant="secondary-brand"
              size="large"
              onPress={() => setShowCart(!showCart)}
            >
              <IconBag />
            </Button>
            {!!getNumberOfDefects(request) && (
              <InlineText size="200">{getNumberOfDefects(request)}</InlineText>
            )}
          </div>
        )}
        {request && globalStep === 'checkout' && (
          <Button variant="secondary-brand" size="medium" onPress={() => setGlobalStep('create')}>
            <Trans id="client.new.header.cancel">Cancel</Trans>
          </Button>
        )}
      </BrandCreationLayoutHeader>
      <main className={block()}>
        <ErrorBoundary>
          {isRequestLoading ? null : request ? (
            <BrandRequestContextProvider
              request={request}
              workflow={workflow}
              productAttributesData={productAttributesByArticle ?? []}
            >
              {globalStep === 'create' && (
                <CreateRequestSteps
                  showCart={showCart}
                  setShowCart={setShowCart}
                  onValidateRequest={() => setGlobalStep('checkout')}
                />
              )}
              {globalStep === 'checkout' && <Checkout onEditCart={() => setGlobalStep('create')} />}
            </BrandRequestContextProvider>
          ) : (
            <Welcome />
          )}
        </ErrorBoundary>
      </main>
    </BrandCreationLayout>
  );
};

const CreateRequestSteps = ({
  showCart,
  setShowCart,
  onValidateRequest,
}: {
  showCart: boolean;
  setShowCart: (showCart: boolean) => void;
  onValidateRequest: () => void;
}) => {
  const navigate = useNavigate();
  const { request } = useBrandRequestContext();

  const [step, setStep] = useState<'settings' | 'article' | 'service' | 'service-added'>(() => {
    const hasStartedFirstArticle = request.articles.some(
      (article) => !!article.product || !!article.productAttributeValues.length
    );

    if (!hasStartedFirstArticle) {
      return request.storeId ? 'settings' : 'article';
    } else {
      const latestArticleHasDefects = hasDefects(request.articles.at(-1)!);

      return latestArticleHasDefects ? 'service-added' : 'article';
    }
  });

  const proofOfPurchaseQueries = useMediaQueries(
    request.articles.map((article) => ({
      types: ['proof-of-purchase'],
      articleId: article?.id,
      limit: 100,
    }))
  );

  const proofOfPurchaseMedia = proofOfPurchaseQueries.flatMap((query) => query.data?.media ?? []);

  const [currentArticleId, setCurrentArticleId] = useState(request.articles.at(-1)?.id);
  const [currentDefectIds, setCurrentDefectIds] = useState<string[]>([]);

  const currentArticle = request.articles.find((article) => article.id === currentArticleId);

  const { mutateAsync: createDraftRequestArticle } = useCreateDraftRequestArticle(request.id);

  const addNewArticle = async () => {
    const { id: newArticleId } = await createDraftRequestArticle();

    setCurrentArticleId(newArticleId);
    setStep('article');
    setShowCart(false);
  };

  const onArticleDeleted = async (deletedArticleId: string) => {
    if (deletedArticleId !== currentArticleId) {
      return;
    }

    const remainingArticleIds = request.articles
      .filter(({ id }) => id !== deletedArticleId)
      .map(({ id }) => id);

    if (remainingArticleIds.length) {
      setStep('service-added');
      setCurrentArticleId(remainingArticleIds.at(-1));
      setShowCart(true);
    } else {
      await addNewArticle();
    }

    setCurrentDefectIds([]);
  };

  if (!currentArticleId || !currentArticle) {
    return null;
  }

  const currentDefects = currentArticle.snapshot.articleDefects.filter((defect) =>
    currentDefectIds.includes(defect.id)
  );

  return (
    <>
      {step === 'settings' && <SettingsForm onContinue={() => setStep('article')} />}
      {step === 'article' && (
        <ArticleForm
          onContinue={() => setStep(hasDefects(currentArticle) ? 'service-added' : 'service')}
          onBack={() => {
            if (request.articles.length === 1 && !hasDefects(currentArticle)) {
              if (request.storeId) {
                setStep('settings');
              } else {
                window.localStorage.removeItem(REQUEST_IN_PROGRESS_LOCAL_STORAGE_KEY);
                navigate(`/brand/${request.organization.slug}/requests/new`, { replace: true });
              }
            } else if (hasDefects(currentArticle)) {
              setStep('service-added');
              setShowCart(true);
            } else {
              setStep('service');
            }
          }}
          article={currentArticle}
          proofOfPurchaseMedia={proofOfPurchaseMedia.filter(
            (media) => media.articleId === currentArticle.id
          )}
          key={currentArticle.id}
        />
      )}
      {step === 'service' && (
        <DefectsForm
          initialDefects={currentDefects}
          article={currentArticle}
          onEditArticle={() => {
            setStep('article');
          }}
          onDeleteArticle={() => {
            onArticleDeleted(currentArticleId);
          }}
          onSaveDefects={() => {
            setCurrentDefectIds([]);
            setStep('service-added');
          }}
          onCancel={() => {
            setCurrentDefectIds([]);
            setStep('service-added');
          }}
        />
      )}
      {step === 'service-added' && currentArticle && (
        <AddAnotherDefect
          article={currentArticle}
          onEditArticle={() => {
            setStep('article');
          }}
          onDeleteArticle={() => {
            onArticleDeleted(currentArticleId);
          }}
          onYes={() => {
            setCurrentDefectIds([]);
            setStep('service');
          }}
          onNo={() => {
            setShowCart(true);
          }}
        />
      )}
      <Cart
        isOpen={showCart}
        onOpenChange={setShowCart}
        onAddArticle={() => {
          addNewArticle();
        }}
        onAddDefectsToArticle={(articleId) => {
          setCurrentArticleId(articleId);
          setStep('service');
          setShowCart(false);
        }}
        onEditDefectsOnArticle={({ articleId, defectIds }) => {
          setStep('service');
          setCurrentArticleId(articleId);
          setCurrentDefectIds(defectIds);
          setShowCart(false);
        }}
        onEditArticle={(articleId) => {
          setCurrentArticleId(articleId);
          setStep('article');
          setShowCart(false);
        }}
        onDeleteAllArticles={() => {
          addNewArticle();
        }}
        onDeleteArticle={(articleId) => {
          onArticleDeleted(articleId);
        }}
        onValidateRequest={onValidateRequest}
      />
    </>
  );
};

export default New;
